import { useEffect, useRef, useState, useCallback } from 'react';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { useStaggerReveal } from '@/hooks/useStaggerReveal';

const reasons = [
  {
    icon: 'ri-flashlight-line',
    title: 'Intelligence out front',
    description:
      'AI leads the build instead of trailing it, so the smart part drives the whole system from day one.',
  },
  {
    icon: 'ri-user-voice-line',
    title: 'One team one contact',
    description:
      'The whole build sits with one team — one number to call when it matters. No handoffs, no gaps.',
  },
  {
    icon: 'ri-shapes-line',
    title: 'Built to fit',
    description:
      'We shape the code to your business, not your business to the code. Every system is tailor-made.',
  },
  {
    icon: 'ri-chat-3-line',
    title: 'Straight to the engineers',
    description:
      'You talk to the people who wrote your system, not a ticket queue or a middleman.',
  },
];

export default function WhyAInnovationSection() {
  const [bgOffset, setBgOffset] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  const rafRef = useRef<number>(0);
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal({ threshold: 0.2 });
  const { ref: cardsRef, isVisible: cardsVisible } = useStaggerReveal({ threshold: 0.1 });

  const handleScroll = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      const el = sectionRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        const height = el.offsetHeight;
        if (rect.bottom > 0 && rect.top < window.innerHeight) {
          const progress = Math.max(0, -rect.top + window.innerHeight) / (height + window.innerHeight);
          setBgOffset(progress * 80);
        }
      }
      rafRef.current = 0;
    });
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [handleScroll]);

  return (
    <section
      ref={sectionRef}
      id="why-ainnovation" className="relative py-24 md:py-36 bg-background-950 overflow-hidden">
        {/* Background image with parallax + slow zoom */}
        <div className="absolute inset-0 will-change-transform">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20dark%20technology%20background%20with%20subtle%20glowing%20grid%20lines%20and%20nodes%2C%20deep%20black%20base%20with%20minimal%20aurora%20green%20and%20amber%20light%20accents%2C%20cinematic%20dark%20atmosphere%2C%20very%20clean%20minimal%20composition%2C%20no%20text&width=1800&height=900&seq=cta-banner-dark-tech&orientation=landscape"
            alt=""
            className="w-full h-[calc(100%+80px)] object-cover object-top animate-hero-bg-zoom"
            style={{ transform: `translateY(${-bgOffset}px)` }}
            width={1800}
            height={900}
          />
        </div>

        {/* Gradient overlay for depth */}
        <div className="absolute inset-0 bg-gradient-to-b from-background-950/70 via-background-950/40 to-background-950/90" />

        {/* Accent glow spot */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full blur-[120px] animate-breathe"
          style={{ background: 'oklch(var(--primary-500) / 0.06)' }}
        />

      <div className="relative z-10 w-full max-w-6xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div
          ref={headerRef}
          className={`mb-16 md:mb-24 text-center transition-all duration-1000 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <h2 className="mt-6 font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.05] tracking-tight">
            Built different
            <br />
            <span className="text-primary-400">Built better</span>
          </h2>
        </div>

        {/* Cards grid */}
        <div
          ref={cardsRef}
          className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 stagger-reveal ${cardsVisible ? 'visible' : ''}`}
        >
          {reasons.map((reason, index) => (
            <div
              key={index}
              className="group relative bg-background-900 rounded-2xl p-7 md:p-8 border border-white/5 hover:border-primary-500/30 transition-all duration-500 cursor-pointer card-lift"
            >
              {/* Icon */}
              <div className="relative w-12 h-12 rounded-xl bg-primary-500/10 flex items-center justify-center mb-6 group-hover:bg-primary-500/20 transition-colors duration-300">
                <span className="w-6 h-6 flex items-center justify-center">
                  <i className={`${reason.icon} text-xl text-primary-400`} />
                </span>
              </div>

              <h3 className="font-heading text-lg font-semibold text-white mb-3 relative z-10">
                {reason.title}
              </h3>
              <p className="text-white/40 text-sm leading-relaxed relative z-10">
                {reason.description}
              </p>

              {/* Hover accent dot */}
              <div className="absolute bottom-0 left-0 w-full h-1 bg-primary-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left rounded-b-2xl" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}